/**
 * Candidate saved jobs / applications — labels and badge variants for match bands.
 * Thresholds live in match-bands.ts (keep in sync with API bands.py).
 */

import {
  normalizeMatchBand,
  type MatchBand,
  type MatchBandInput,
} from '@/lib/match-bands'

export type CandidateMatchBand = MatchBand | null

/** Provenance caption for insights judged from a marketplace listing description. */
export const MARKETPLACE_INSIGHT_CAPTION =
  "Based on the listing description — external jobs don't include full role data."

export const CANDIDATE_MATCH_BAND_LABELS: Record<MatchBand, string> = {
  strong: 'Strong match',
  good: 'Good match',
  mixed: 'Worth a look',
  limited: 'Limited fit',
  poor: 'Poor fit',
}

export function candidateMatchBandLabel(band: MatchBandInput): string | null {
  const normalized = normalizeMatchBand(band)
  if (!normalized) return null
  return CANDIDATE_MATCH_BAND_LABELS[normalized]
}

export type CandidateMatchBandBadgeVariant =
  | 'matchStrong'
  | 'matchGood'
  | 'matchMixed'
  | 'matchLimited'
  | 'matchPoor'
  | 'outline'

export function candidateMatchBandBadgeVariant(
  band: MatchBandInput,
): CandidateMatchBandBadgeVariant {
  const normalized = normalizeMatchBand(band)
  if (normalized === 'strong') return 'matchStrong'
  if (normalized === 'good') return 'matchGood'
  if (normalized === 'mixed') return 'matchMixed'
  if (normalized === 'limited') return 'matchLimited'
  if (normalized === 'poor') return 'matchPoor'
  return 'outline'
}

/** Strong and good get card/fit emphasis; weaker bands stay quieter. */
export function candidateMatchBandIsEmphasized(band: MatchBandInput): boolean {
  const normalized = normalizeMatchBand(band)
  return normalized === 'strong' || normalized === 'good'
}

/** Fit-insight box chrome keyed to band (positive bands only). */
export function candidateMatchBandFitChromeClass(band: MatchBandInput): string | undefined {
  const normalized = normalizeMatchBand(band)
  if (normalized === 'strong') return 'border-success/40 bg-success/10'
  if (normalized === 'good') return 'border-primary/40 bg-primary/10'
  return undefined
}
